import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TestEndpointRequest {
  deviceId: string;
}

interface TestEndpointResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export const useTestApiEndpoint = () => {
  return useMutation({
    mutationFn: async ({ deviceId }: TestEndpointRequest): Promise<TestEndpointResponse> => {
      // First get the BuildShip token
      const { data: tokenResponse, error: tokenError } = await supabase.functions.invoke('get-buildship-token');
      
      if (tokenError) {
        throw new Error(`Failed to get auth token: ${tokenError.message}`);
      }

      if (!tokenResponse?.success || !tokenResponse?.token) {
        throw new Error('Failed to get valid auth token');
      }

      // Call the BuildShip API to simulate endpoint
      const response = await fetch('https://api.snapshot.systems/simulateApiEndpoint', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenResponse.token}`,
        },
        body: JSON.stringify({
          deviceId: deviceId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `API call failed with status ${response.status}`);
      }

      return result;
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message || 'Test successful! Latest device data sent to your endpoint.');
      } else {
        toast.error(data.error || 'Test completed but with issues.');
      }
    },
    onError: (error) => {
      console.error('Test endpoint error:', error);
      toast.error(`Test failed: ${error.message}`);
    },
  });
};